import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IndentDecrease, IndentIncrease } from 'lucide-react'
import { ButtonGroup } from '@/src/renderer/components/ui/ButtonGroup'
import { Popover, PopoverAnchor, PopoverContent } from '@/src/renderer/components/ui/popover'
import type { Task } from '@/src/renderer/lib/db/models'
import { NestPicker } from '../NestPicker'
import { useNestActions } from '../../hooks/useNestActions'
import { useMoveTargets } from '../../hooks/useMoveTargets'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'

export interface DetailsSubtaskActionsProps {
  /** Die Unteraufgabe, die gerade in den Details steht. */
  subtask: Task
  /** Ihre Aufgabe. */
  parent: Task
}

/**
 * **Herauslösen und Umhängen in den Details einer Unteraufgabe** (#204, Grill 9) — zwei
 * Knöpfe im Kopf, neben den Pfeilen. „Umhängen in …“ öffnet dieselbe Auswahl wie das
 * Aufgaben-Menü: die offenen Aufgaben derselben Liste, nach Abschnitt.
 *
 * Die Details stehen neben der Liste, nicht in ihr — die Kandidaten kommen deshalb aus der
 * Datenquelle, nicht von der Liste.
 */
export const DetailsSubtaskActions: React.FC<DetailsSubtaskActionsProps> = ({ subtask, parent }) => {
  const { t } = useTranslation()
  const { nest, unnest } = useNestActions()
  const shortcutText = useShortcutText()
  const [open, setOpen] = useState(false)

  const groups = useMoveTargets(parent, subtask.recurrence != null)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="task-details__subtask-actions">
          <ButtonGroup
            iconOnly
            aria-label={t('taskList.nest.menu')}
            items={[
              { value: 'unnest', label: `${t('taskList.nest.unnest')} (${shortcutText('⇧⌥↓')})`, icon: <IndentDecrease aria-hidden="true" /> },
              { value: 'move', label: t('taskList.nest.move'), icon: <IndentIncrease aria-hidden="true" /> },
            ]}
            onSelect={value => {
              if (value === 'unnest') void unnest([subtask.id])
              else setOpen(true)
            }}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent className="context-menu-content task-details__move-panel" align="end">
        <NestPicker
          groups={groups}
          onSelect={parentId => {
            setOpen(false)
            void nest([subtask.id], parentId, { select: false })
          }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}
